import { apiSlice } from './apiSlice'
import { ENDPOINTS } from '../../lib/endpoints'
import type { LeaderboardPeriod } from './leaderboardApi'

export interface ModelPricingConfig {
  models: Record<string, { in: number; out: number }>
  default: { in: number; out: number }
}

export interface AgentSafetyConfig {
  preamble: string
  reminder: string
}

export interface UserAiUsageRow {
  userId:           string
  displayName:      string
  avatarUrl:        string | null
  email:            string
  promptTokens:     number
  completionTokens: number
  totalTokens:      number
  requestCount:     number
  costUsd:          number
}

export interface AdminAiUsageResponse {
  data:        UserAiUsageRow[]
  hasMore:     boolean
  nextOffset:  number | null
  totals: {
    promptTokens:     number
    completionTokens: number
    totalTokens:      number
    requestCount:     number
    costUsd:          number
  }
}

export interface MonetizationSettings {
  agentSharePct: number
}

export interface MonetizationReportRow {
  agentId:       string
  agentName:     string
  agentIcon:     string | null
  agentType:     string
  developerId:   string | null
  developerName: string | null
  developerHasBankDetails: boolean
  unlockCount:   number
  totalPaise:    number
  agentPaise:    number
  platformPaise: number
}

export interface MonetizationReportResponse {
  totals: {
    unlockCount:   number
    totalPaise:    number
    agentPaise:    number
    platformPaise: number
  }
  byAgent: MonetizationReportRow[]
}

export interface PayoutSummaryRow {
  developerId:       string
  developerName:     string
  totalEarnedPaise:  number
  totalPaidPaise:    number
  outstandingPaise:  number
  hasBankDetails:    boolean
}

export interface RagEarningsRow {
  agentId:             string
  agentName:           string
  developerName:       string
  planName:            string
  subscriptionStatus:  string
  paymentCount:        number
  totalCollectedPaise: number
  earningsPaise:       number
  gstPaise:            number
  lastPaidAt:          string
}

export interface RagEarningsResponse {
  totals: {
    totalCollectedPaise: number
    earningsPaise:       number
    gstPaise:            number
    paymentCount:        number
  }
  byAgent: RagEarningsRow[]
}

export interface PayoutRecord {
  id:            string
  developerId:   string
  developerName: string
  amountPaise:   number
  amountUsd:     number | null
  periodFrom:    string
  periodTo:      string
  note:          string | null
  paidAt:        string
}

export interface SubscriptionConfig {
  tokenTracking: boolean

  discussionFreeLimit: number
  chatFreeLimit: number
  discussionFreeTokens: number
  chatFreeTokens: number

  discussionProLimit: number
  chatProLimit: number
  discussionProTokens: number
  chatProTokens: number

  discussionModal: {
    title: string
    body: string
    note: string
  }
  chatBanner: {
    title: string
    tokenBody: string
    requestBody: string
    usageTokenLabel: string
    usageRequestLabel: string
  }
}

export interface AgentAdminRecord {
  id: string
  name: string
  description: string
  systemPrompt: string
  model: string
  provider: string
  categoryIds: string[]
  suggestedCategoryName?: string | null
  status: string
  createdAt: string
  updatedAt: string
}

export interface CategoryAdminRecord {
  id: string
  name: string
  slug: string
  description: string | null
  color: string | null
  icon: string | null
  isActive: boolean
  sortOrder: number
}

export interface Agent {
  id: string
  name: string
  icon: string
  color: string
  description: string
  categoryIds: string[]
  model: string
  provider: string
  status: 'online' | 'idle'
  postCount: number
  lastActiveAt: string | null
  rank: number
}

export interface ForumStats {
  topicCount: number
  postCount: number
  agentCount: number
}

export interface FeedbackResponseItem {
  commentId: string
  count: number
  agentName: string
  contentPreview: string
  discussionId: string | null
  discussionTitle: string | null
}

export interface FeedbackStats {
  byAgent: {
    helpful:    { agentId: string; agentName: string; count: number }[]
    notHelpful: { agentId: string; agentName: string; count: number }[]
  }
  topResponses: {
    helpful:    FeedbackResponseItem[]
    notHelpful: FeedbackResponseItem[]
  }
}

export const agentsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAgents: builder.query<{ agents: Agent[] }, void>({
      query: () => ENDPOINTS.ai.agents,
      providesTags: ['Agent'],
    }),
    getTopAgents: builder.query<{ agents: Agent[]; total: number; page: number; limit: number; hasMore: boolean }, { period: LeaderboardPeriod; page?: number }>({
      query: ({ period, page = 1 }) => `${ENDPOINTS.ai.topAgents}?period=${period}&page=${page}&limit=10`,
      providesTags: ['Agent'],
    }),
    getTopContributors: builder.query<{
      agents: { rank: number; id: string; name: string; icon: string; color: string; postCount: number; type: 'ai' }[]
      users:  { rank: number; id: string; name: string; avatarUrl: string | null; postCount: number; type: 'user' }[]
    }, void>({
      query: () => ENDPOINTS.ai.topContributors,
      providesTags: ['Agent'],
    }),
    getForumStats: builder.query<ForumStats, void>({
      query: () => ENDPOINTS.ai.stats,
    }),

    // ── Admin CRUD ─────────────────────────────────────────────
    getAdminAgents: builder.query<{ agents: AgentAdminRecord[] }, void>({
      query: () => ENDPOINTS.ai.adminAgents,
      providesTags: ['Agent'],
    }),
    createAgent: builder.mutation<{ agent: AgentAdminRecord }, Partial<AgentAdminRecord>>({
      query: (body) => ({ url: ENDPOINTS.ai.adminAgents, method: 'POST', body }),
      invalidatesTags: ['Agent'],
    }),
    updateAgent: builder.mutation<{ agent: AgentAdminRecord }, { id: string; body: Partial<AgentAdminRecord> }>({
      query: ({ id, body }) => ({ url: ENDPOINTS.ai.adminAgent(id), method: 'PUT', body }),
      invalidatesTags: ['Agent'],
    }),
    deleteAgent: builder.mutation<void, string>({
      query: (id) => ({ url: ENDPOINTS.ai.adminAgent(id), method: 'DELETE' }),
      invalidatesTags: ['Agent'],
    }),
    getAdminCategories: builder.query<{ categories: CategoryAdminRecord[] }, void>({
      query: () => ENDPOINTS.ai.adminCategories,
      providesTags: ['Category'],
    }),
    getFeedbackStats: builder.query<FeedbackStats, void>({
      query: () => ENDPOINTS.ai.feedbackStats,
      extraOptions: { service: 'ai' },
    }),
    getCostStats: builder.query<{
      totals30d: { requests: number; tokensIn: number; tokensOut: number; costUsd: number; avgDurationMs: number }
      totals7d:  { requests: number; costUsd: number }
      byAgent: { agentId: string; agentName: string; requests: number; tokensIn: number; tokensOut: number; costUsd: number; avgDurationMs: number }[]
      dailyCosts: { date: string; cost: number; requests: number }[]
    }, void>({
      query: () => ENDPOINTS.ai.costStats,
    }),
    createCategory: builder.mutation<{ category: CategoryAdminRecord }, Partial<CategoryAdminRecord>>({
      query: (body) => ({ url: ENDPOINTS.ai.adminCategories, method: 'POST', body }),
      invalidatesTags: ['Category'],
    }),
    updateCategory: builder.mutation<{ category: CategoryAdminRecord }, { id: string; body: Partial<CategoryAdminRecord> }>({
      query: ({ id, body }) => ({ url: ENDPOINTS.ai.adminCategory(id), method: 'PUT', body }),
      invalidatesTags: ['Category'],
    }),
    deleteCategory: builder.mutation<void, string>({
      query: (id) => ({ url: ENDPOINTS.ai.adminCategory(id), method: 'DELETE' }),
      invalidatesTags: ['Category'],
    }),
    getSpendingCapConfig: builder.query<{ config: SubscriptionConfig }, void>({
      query: () => ENDPOINTS.ai.spendingCapConfig,
      providesTags: ['Agent'],
      keepUnusedDataFor: 3600,
    }),
    updateSpendingCapConfig: builder.mutation<{ config: SubscriptionConfig }, SubscriptionConfig>({
      query: (body) => ({ url: ENDPOINTS.ai.spendingCapConfig, method: 'PUT', body }),
      invalidatesTags: ['Agent'],
    }),
    getAdminAiUsage: builder.query<AdminAiUsageResponse, { period?: string; agentId?: string; sortBy?: string; offset?: number; from?: string; to?: string; userId?: string }>({
      query: ({ period, agentId, sortBy = 'tokens', offset = 0, from, to, userId }) => {
        const params = new URLSearchParams({ sortBy, offset: String(offset) })
        if (from && to) { params.set('from', from); params.set('to', to) }
        else if (period) params.set('period', period)
        if (agentId) params.set('agentId', agentId)
        if (userId)  params.set('userId',  userId)
        return `${ENDPOINTS.users.adminAiUsage}?${params}`
      },
    }),
    searchAdminUsers: builder.query<{ data: { id: string; email: string; displayName: string; avatarUrl: string | null }[]; nextCursor: string | null; hasMore: boolean }, { q?: string; cursor?: string }>({
      query: ({ q = '', cursor }) => {
        const params = new URLSearchParams({ q, limit: '10' })
        if (cursor) params.set('cursor', cursor)
        return `${ENDPOINTS.users.adminSearch}?${params}`
      },
    }),
    getAdminUserAiUsage: builder.query<{
      userId: string
      from: string | null
      to: string | null
      byModel: {
        model: string
        provider: string
        requestType: string
        promptTokens: number
        completionTokens: number
        totalTokens: number
        requestCount: number
        costUsd: number
      }[]
    }, { userId: string; from?: string; to?: string; agentId?: string }>({
      query: ({ userId, from, to, agentId }) => {
        const params = new URLSearchParams()
        if (from)    params.set('from', from)
        if (to)      params.set('to', to)
        if (agentId) params.set('agentId', agentId)
        return `${ENDPOINTS.users.adminUserAiUsage(userId)}?${params}`
      },
    }),
    getModelPricingConfig: builder.query<{ config: ModelPricingConfig }, void>({
      query: () => ENDPOINTS.ai.modelPricing,
      providesTags: ['Agent'],
    }),
    updateModelPricingConfig: builder.mutation<{ config: ModelPricingConfig }, ModelPricingConfig>({
      query: (body) => ({ url: ENDPOINTS.ai.modelPricing, method: 'PUT', body }),
      invalidatesTags: ['Agent'],
    }),
    getAgentSafetyConfig: builder.query<{ config: AgentSafetyConfig; defaults: AgentSafetyConfig }, void>({
      query: () => ENDPOINTS.ai.agentSafetyConfig,
      providesTags: ['Agent'],
    }),
    updateAgentSafetyConfig: builder.mutation<{ config: AgentSafetyConfig }, AgentSafetyConfig>({
      query: (body) => ({ url: ENDPOINTS.ai.agentSafetyConfig, method: 'PUT', body }),
      invalidatesTags: ['Agent'],
    }),
    getMonetizationSettings: builder.query<MonetizationSettings, void>({
      query: () => ENDPOINTS.monetization.adminSettings,
      providesTags: ['Agent'],
    }),
    updateMonetizationSettings: builder.mutation<MonetizationSettings, MonetizationSettings>({
      query: (body) => ({ url: ENDPOINTS.monetization.adminSettings, method: 'PUT', body }),
      invalidatesTags: ['Agent'],
    }),
    getMonetizationReport: builder.query<MonetizationReportResponse, { from: string; to: string; agentId?: string }>({
      query: ({ from, to, agentId }) => {
        const params = new URLSearchParams({ from, to })
        if (agentId) params.set('agentId', agentId)
        return `${ENDPOINTS.monetization.adminReport}?${params}`
      },
    }),
    getPayoutSummary: builder.query<{ summary: PayoutSummaryRow[]; platformEarningsPaise: number }, void>({
      query: () => ENDPOINTS.monetization.adminPayoutSummary,
      providesTags: ['Payout'],
    }),
    getWebhookEarnings: builder.query<MonetizationReportResponse, void>({
      query: () => ENDPOINTS.monetization.adminWebhookEarnings,
    }),
    getRagEarnings: builder.query<RagEarningsResponse, void>({
      query: () => ENDPOINTS.monetization.adminRagEarnings,
    }),
    getPayouts: builder.query<{ payouts: PayoutRecord[]; hasMore: boolean; nextOffset: number | null }, { developerId?: string; from?: string; to?: string; offset?: number }>({
      query: (params) => {
        const search = new URLSearchParams()
        if (params.developerId) search.set('developerId', params.developerId)
        if (params.from) search.set('from', params.from)
        if (params.to) search.set('to', params.to)
        if (params.offset) search.set('offset', String(params.offset))
        const qs = search.toString()
        return qs ? `${ENDPOINTS.monetization.adminPayouts}?${qs}` : ENDPOINTS.monetization.adminPayouts
      },
      providesTags: ['Payout'],
    }),
    createPayout: builder.mutation<{ payout: PayoutRecord }, { developerId: string; amountPaise: number; amountUsd?: number; periodFrom: string; periodTo: string; note?: string }>({
      query: (body) => ({ url: ENDPOINTS.monetization.adminPayouts, method: 'POST', body }),
      invalidatesTags: ['Payout'],
    }),
    deletePayout: builder.mutation<void, string>({
      query: (id) => ({ url: ENDPOINTS.monetization.adminPayout(id), method: 'DELETE' }),
      invalidatesTags: ['Payout'],
    }),
  }),
})

export const {
  useGetAgentsQuery,
  useGetTopAgentsQuery,
  useGetTopContributorsQuery,
  useGetForumStatsQuery,
  useGetAdminAgentsQuery,
  useCreateAgentMutation,
  useUpdateAgentMutation,
  useDeleteAgentMutation,
  useGetAdminCategoriesQuery,
  useGetFeedbackStatsQuery,
  useGetCostStatsQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
  useGetSpendingCapConfigQuery,
  useUpdateSpendingCapConfigMutation,
  useGetAdminAiUsageQuery,
  useSearchAdminUsersQuery,
  useGetAdminUserAiUsageQuery,
  useGetModelPricingConfigQuery,
  useUpdateModelPricingConfigMutation,
  useGetAgentSafetyConfigQuery,
  useUpdateAgentSafetyConfigMutation,
  useGetMonetizationSettingsQuery,
  useUpdateMonetizationSettingsMutation,
  useGetMonetizationReportQuery,
  useGetPayoutSummaryQuery,
  useGetRagEarningsQuery,
  useGetWebhookEarningsQuery,
  useGetPayoutsQuery,
  useCreatePayoutMutation,
  useDeletePayoutMutation,
} = agentsApi
